/**
 * TrainAgentModal Component
 * Modal para treinar o agente (processamento RAG)
 * Processa todas as fontes de conhecimento e gera embeddings
 */

import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Modal, Button, Spinner, Badge, ProgressBar, Alert } from 'react-bootstrap';
import {
  IconBrain,
  IconFileText,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconRefresh,
  IconSparkles,
  IconCloudUpload,
  IconDatabase,
  IconClock,
  IconChartBar,
  IconSquareCheck,
  IconSquare,
  IconSelectAll,
} from '@tabler/icons-react';
import AgentsFlowService from '../services/AgentsFlowService';
import NotificationsContext from '../../../contexts/NotificationsContext';
import './TrainAgentModal.css';

// Status do treinamento
const TRAINING_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  TRAINING: 'training',
  COMPLETED: 'completed',
  ERROR: 'error',
  NO_SOURCES: 'no_sources',
};

// Status de cada fonte
const SOURCE_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error',
};

const TrainAgentModal = ({ show, onHide, agent, onTrainingComplete }) => {
  const { setNotify } = useContext(NotificationsContext);
  const [status, setStatus] = useState(TRAINING_STATUS.IDLE);
  const [sources, setSources] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [trainingInfo, setTrainingInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [pollingCount, setPollingCount] = useState(0);
  const pollingRef = useRef(null);
  const maxPollingAttempts = 60; // 2 minutos com polling de 2 segundos

  // Carregar fontes de conhecimento
  const loadKnowledgeSources = useCallback(async () => {
    if (!agent?.id) return;

    try {
      setStatus(TRAINING_STATUS.LOADING);
      const response = await AgentsFlowService.getKnowledge(agent.id);
      const knowledgeSources = response.data || [];

      // Filtrar apenas fontes não treinadas
      const pendingSources = knowledgeSources.filter((s) => !s.is_processed);

      if (pendingSources.length === 0) {
        setStatus(TRAINING_STATUS.NO_SOURCES);
        setSources([]);
        setSelectedIds(new Set());
      } else {
        setSources(pendingSources);
        // Selecionar todas por padrão
        setSelectedIds(new Set(pendingSources.map((s) => s.id)));
        setStatus(TRAINING_STATUS.IDLE);
      }
    } catch (error) {
      console.error('Erro ao carregar fontes:', error);
      setErrorMessage('Erro ao carregar fontes de conhecimento');
      setStatus(TRAINING_STATUS.ERROR);
    }
  }, [agent?.id]);

  // Toggle seleção de uma fonte
  const toggleSource = useCallback((sourceId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
      }
      return next;
    });
  }, []);

  // Selecionar/deselecionar todas
  const toggleAll = useCallback(() => {
    if (selectedIds.size === sources.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sources.map((s) => s.id)));
    }
  }, [selectedIds.size, sources]);

  // Iniciar treinamento
  const startTraining = async () => {
    if (!agent?.id || selectedIds.size === 0) return;

    try {
      setStatus(TRAINING_STATUS.TRAINING);
      setErrorMessage('');
      setPollingCount(0);

      // Enviar apenas IDs selecionados (ou null se todos selecionados)
      const knowledgeIds = selectedIds.size === sources.length
        ? null
        : Array.from(selectedIds);

      const response = await AgentsFlowService.trainAgent(agent.id, knowledgeIds);
      const data = response.data;

      setTrainingInfo(data);

      // Iniciar polling do status
      startPolling();
    } catch (error) {
      console.error('Erro ao iniciar treinamento:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao iniciar treinamento';
      setErrorMessage(errorMsg);
      setStatus(TRAINING_STATUS.ERROR);
    }
  };

  // Verificar status do treinamento
  const checkTrainingStatus = useCallback(async () => {
    if (!agent?.id) return;

    try {
      const response = await AgentsFlowService.getTrainingStatus(agent.id);
      const data = response.data;

      setTrainingInfo(data);

      // Atualizar status das fontes
      if (data.sources) {
        setSources(prevSources =>
          prevSources.map(source => {
            const sourceStatus = data.sources.find(
              s => s.knowledge_id === source.id
            );
            if (sourceStatus) {
              return {
                ...source,
                training_status: sourceStatus.status,
                chunk_count: sourceStatus.chunk_count,
                error: sourceStatus.error,
              };
            }
            return source;
          })
        );
      }

      // Verificar se o treinamento terminou
      if (data.status === 'completed') {
        stopPolling();
        setStatus(TRAINING_STATUS.COMPLETED);

        setNotify({
          title: 'Treinamento concluído!',
          message: `O agente "${agent.name}" foi treinado com ${data.total_chunks || 0} fragmentos de conhecimento.`,
          type: 'success',
          visible: true,
        });

        if (onTrainingComplete) {
          onTrainingComplete(data);
        }
      } else if (data.status === 'error') {
        stopPolling();
        setStatus(TRAINING_STATUS.ERROR);
        const sourceErrors = data.sources?.filter(s => s.error).map(s => s.error) || [];
        setErrorMessage(sourceErrors[0] || 'Todas as fontes falharam no processamento');
      } else if (data.status === 'partial') {
        if (data.pending === 0) {
          // Todas terminaram (mix de concluídas + erros) — parar polling
          stopPolling();
          const hasErrors = data.errors > 0;
          if (hasErrors) {
            setStatus(TRAINING_STATUS.ERROR);
            const sourceErrors = data.sources?.filter(s => s.error).map(s => s.error) || [];
            setErrorMessage(
              `${data.processed} fonte(s) treinada(s), ${data.errors} com erro. ` +
              (sourceErrors[0] || '')
            );
          } else {
            setStatus(TRAINING_STATUS.COMPLETED);
          }
        } else {
          // Ainda há pendentes, continuar polling
          setPollingCount(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      // Não parar polling por erro de rede, tentar novamente
      setPollingCount(prev => prev + 1);
    }
  }, [agent?.id, agent?.name, onTrainingComplete, setNotify]);

  // Iniciar polling
  const startPolling = useCallback(() => {
    stopPolling(); // Limpar polling anterior
    pollingRef.current = setInterval(() => {
      checkTrainingStatus();
    }, 2000); // Polling a cada 2 segundos
  }, [checkTrainingStatus]);

  // Parar polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Verificar limite de polling
  useEffect(() => {
    if (pollingCount >= maxPollingAttempts) {
      stopPolling();
      setStatus(TRAINING_STATUS.ERROR);
      setErrorMessage('Tempo limite de treinamento excedido. Verifique o status novamente.');
    }
  }, [pollingCount, stopPolling]);

  // Carregar fontes quando modal abre
  useEffect(() => {
    if (show && agent?.id) {
      loadKnowledgeSources();
    }

    return () => {
      stopPolling();
    };
  }, [show, agent?.id, loadKnowledgeSources, stopPolling]);

  // Limpar estado quando modal fecha
  const handleClose = () => {
    stopPolling();
    setStatus(TRAINING_STATUS.IDLE);
    setTrainingInfo(null);
    setErrorMessage('');
    setPollingCount(0);
    setSelectedIds(new Set());
    onHide();
  };

  // Calcular progresso (apenas fontes selecionadas)
  const calculateProgress = () => {
    const selected = sources.filter((s) => selectedIds.has(s.id));
    if (!selected.length) return 0;
    const completed = selected.filter(
      (s) => s.training_status === SOURCE_STATUS.COMPLETED
    ).length;
    return Math.round((completed / selected.length) * 100);
  };

  // Renderizar ícone de status da fonte
  const renderSourceStatusIcon = (source) => {
    const sourceStatus = source.training_status || source.status;

    switch (sourceStatus) {
      case SOURCE_STATUS.COMPLETED:
      case 'completed':
        return <IconCheck size={18} className="text-success" />;
      case SOURCE_STATUS.PROCESSING:
      case 'processing':
        return <Spinner size="sm" animation="border" variant="primary" />;
      case SOURCE_STATUS.ERROR:
      case 'error':
        return <IconX size={18} className="text-danger" />;
      default:
        return <IconClock size={18} className="text-muted" />;
    }
  };

  // Renderizar badge de status
  const renderStatusBadge = (source) => {
    const sourceStatus = source.training_status || source.status;
    const isProcessed = source.is_processed;

    if (isProcessed && sourceStatus !== 'error') {
      return <Badge bg="success">Treinado</Badge>;
    }

    switch (sourceStatus) {
      case SOURCE_STATUS.COMPLETED:
      case 'completed':
        return <Badge bg="success">Concluído</Badge>;
      case SOURCE_STATUS.PROCESSING:
      case 'processing':
        return <Badge bg="primary">Processando</Badge>;
      case SOURCE_STATUS.ERROR:
      case 'error':
        return <Badge bg="danger">Erro</Badge>;
      default:
        return <Badge bg="secondary">Pendente</Badge>;
    }
  };

  // Formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Obter ícone do tipo de arquivo
  const getFileIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return <IconFileText size={24} className="text-danger" />;
      case 'docx':
      case 'doc':
        return <IconFileText size={24} className="text-info" />;
      case 'txt':
      case 'md':
        return <IconFileText size={24} className="text-secondary" />;
      default:
        return <IconFileText size={24} className="text-muted" />;
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      className="train-agent-modal"
      backdrop={status === TRAINING_STATUS.TRAINING ? 'static' : true}
      keyboard={status !== TRAINING_STATUS.TRAINING}
    >
      <Modal.Body className="p-0">
        {/* Header com gradiente do design system */}
        <div className="train-header">
          <div className="train-header-content">
            <div className="train-icon-wrapper">
              <IconBrain size={32} stroke={1.5} />
              <div className="train-sparkle">
                <IconSparkles size={14} />
              </div>
            </div>
            <div className="train-header-text">
              <h2 className="train-title">Treinar Agente</h2>
              <p className="train-subtitle">{agent?.name || 'Agente'}</p>
            </div>
          </div>
          {sources.length > 0 && status !== TRAINING_STATUS.LOADING && (
            <div className="train-header-stats">
              <div className="train-stat">
                <IconCloudUpload size={16} />
                <span>{selectedIds.size}/{sources.length} selecionada{sources.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="train-content">
          {/* Loading State */}
          {status === TRAINING_STATUS.LOADING && (
            <div className="train-loading">
              <Spinner animation="border" variant="primary" />
              <p>Carregando fontes de conhecimento...</p>
            </div>
          )}

          {/* No Sources State */}
          {status === TRAINING_STATUS.NO_SOURCES && (
            <div className="train-empty-state">
              <div className="train-empty-icon">
                <IconCheck size={48} />
              </div>
              <h4>Tudo treinado!</h4>
              <p>
                Todas as fontes de conhecimento já foram processadas.
                Adicione novos arquivos na aba <strong>"Conhecimento"</strong> para treinar novamente.
              </p>
            </div>
          )}

          {/* Sources List */}
          {(status === TRAINING_STATUS.IDLE ||
            status === TRAINING_STATUS.TRAINING ||
            status === TRAINING_STATUS.COMPLETED ||
            status === TRAINING_STATUS.ERROR) &&
            sources.length > 0 && (
            <>
              {/* Info Banner */}
              <div className="train-info-banner">
                <div className="train-info-icon">
                  <IconDatabase size={20} />
                </div>
                <div className="train-info-text">
                  <strong>O que acontece no treinamento?</strong>
                  <p>
                    Os documentos são processados, divididos em fragmentos e convertidos
                    em embeddings vetoriais para busca semântica.
                  </p>
                </div>
              </div>

              {/* Progress Bar (during training) */}
              {status === TRAINING_STATUS.TRAINING && (
                <div className="train-progress-section">
                  <div className="train-progress-header">
                    <span className="train-progress-label">
                      <IconChartBar size={16} />
                      Progresso do treinamento
                    </span>
                    <span className="train-progress-value">{calculateProgress()}%</span>
                  </div>
                  <ProgressBar
                    now={calculateProgress()}
                    animated
                    striped
                    className="train-progress-bar"
                  />
                </div>
              )}

              {/* Sources Grid */}
              <div className="train-sources-section">
                <div className="train-sources-header">
                  <h6>
                    <IconCloudUpload size={18} />
                    Fontes de Conhecimento
                  </h6>
                  <div className="train-sources-header-actions">
                    {status === TRAINING_STATUS.IDLE && (
                      <button
                        className="train-select-all-btn"
                        onClick={toggleAll}
                        type="button"
                      >
                        {selectedIds.size === sources.length ? (
                          <><IconSquareCheck size={16} /> Desmarcar todas</>
                        ) : (
                          <><IconSelectAll size={16} /> Selecionar todas</>
                        )}
                      </button>
                    )}
                    <span className="train-sources-count">{selectedIds.size}</span>
                  </div>
                </div>

                <div className="train-sources-list">
                  {sources.map((source, index) => {
                    const isSelected = selectedIds.has(source.id);
                    const isIdle = status === TRAINING_STATUS.IDLE;
                    return (
                      <div
                        key={source.id}
                        className={`train-source-item ${
                          source.training_status === 'error' ? 'error' : ''
                        } ${
                          source.training_status === 'completed' || source.is_processed
                            ? 'completed' : ''
                        } ${isSelected ? 'selected' : ''} ${
                          !isSelected && isIdle ? 'deselected' : ''
                        }`}
                        style={{ animationDelay: `${index * 0.05}s` }}
                        onClick={isIdle ? () => toggleSource(source.id) : undefined}
                        role={isIdle ? 'button' : undefined}
                      >
                        {/* Checkbox (apenas no modo idle) */}
                        {isIdle && (
                          <div className="train-source-checkbox">
                            {isSelected ? (
                              <IconSquareCheck size={22} className="text-primary" />
                            ) : (
                              <IconSquare size={22} className="text-muted" />
                            )}
                          </div>
                        )}
                        <div className="train-source-icon">
                          {getFileIcon(source.file_type)}
                        </div>
                        <div className="train-source-info">
                          <div className="train-source-name">
                            {source.file_name || source.external_config?.file_name || source.external_url || 'Fonte sem nome'}
                          </div>
                          <div className="train-source-meta">
                            <span>{source.file_type?.toUpperCase() || source.external_config?.mime_type?.split('/').pop() || source.source_type}</span>
                            {source.file_size && (
                              <>
                                <span className="separator">•</span>
                                <span>{formatFileSize(source.file_size)}</span>
                              </>
                            )}
                            {source.chunk_count > 0 && (
                              <>
                                <span className="separator">•</span>
                                <span>{source.chunk_count} fragmentos</span>
                              </>
                            )}
                          </div>
                          {source.error && (
                            <div className="train-source-error">
                              {source.error}
                            </div>
                          )}
                        </div>
                        <div className="train-source-status">
                          {renderSourceStatusIcon(source)}
                          <div className="train-source-badge">
                            {renderStatusBadge(source)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Success Message */}
              {status === TRAINING_STATUS.COMPLETED && (
                <div className="train-result-message success">
                  <div className="train-result-icon">
                    <IconCheck size={24} />
                  </div>
                  <div className="train-result-text">
                    <strong>Treinamento concluído!</strong>
                    <p>
                      O agente está pronto para usar o conhecimento.
                      {trainingInfo?.total_chunks && (
                        <> Foram criados <strong>{trainingInfo.total_chunks}</strong> fragmentos.</>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {status === TRAINING_STATUS.ERROR && errorMessage && (
                <div className="train-result-message error">
                  <div className="train-result-icon">
                    <IconX size={24} />
                  </div>
                  <div className="train-result-text">
                    <strong>Erro no treinamento</strong>
                    <p>{errorMessage}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="train-footer">
          {status === TRAINING_STATUS.COMPLETED ? (
            <Button
              variant="success"
              onClick={handleClose}
              className="train-btn train-btn-success"
            >
              <IconCheck size={18} />
              Concluído
            </Button>
          ) : status === TRAINING_STATUS.ERROR ? (
            <div className="train-footer-actions">
              <Button
                variant="outline-secondary"
                onClick={handleClose}
                className="train-btn"
              >
                Fechar
              </Button>
              <Button
                variant="primary"
                onClick={startTraining}
                className="train-btn train-btn-primary"
              >
                <IconRefresh size={18} />
                Tentar Novamente
              </Button>
            </div>
          ) : status === TRAINING_STATUS.TRAINING ? (
            <Button
              variant="primary"
              disabled
              className="train-btn train-btn-primary"
            >
              <Spinner size="sm" animation="border" />
              Treinando... {calculateProgress()}%
            </Button>
          ) : status === TRAINING_STATUS.NO_SOURCES ? (
            <Button
              variant="secondary"
              onClick={handleClose}
              className="train-btn"
            >
              Fechar
            </Button>
          ) : (
            <div className="train-footer-actions">
              <Button
                variant="outline-secondary"
                onClick={handleClose}
                className="train-btn"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={startTraining}
                disabled={selectedIds.size === 0}
                className="train-btn train-btn-primary"
              >
                <IconBrain size={18} />
                Treinar {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
              </Button>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TrainAgentModal;
